import React, { useMemo, useState } from 'react';
import { Gruppe, Session } from '../types';
import { ApiService } from '../api';
import Icon from './Icon';
import SegmentedControl from './SegmentedControl';
import ActionSheet from './ActionSheet';
import { useToast } from './Toast';

interface GruppeDetailsProps {
  gruppe: Gruppe;
  onSessionSelect: (session: Session) => void;
  onNewSession: () => void;
  onShowLeaderboard: () => void;
  onRefresh: (updatedGruppe?: Gruppe) => void;
}

type Tab = 'overview' | 'sessions' | 'spieler';

const GruppeDetails: React.FC<GruppeDetailsProps> = ({
  gruppe,
  onSessionSelect,
  onNewSession,
  onShowLeaderboard,
  onRefresh,
}) => {
  const [tab, setTab] = useState<Tab>('overview');
  const [newSpielerName, setNewSpielerName] = useState('');
  const [isCreatingSpieler, setIsCreatingSpieler] = useState(false);
  const [deleteSpieler, setDeleteSpieler] = useState<{ id: number; name: string } | null>(null);
  const [deleteSession, setDeleteSessionState] = useState<Session | null>(null);
  const toast = useToast();

  const refresh = async () => {
    try {
      const updated = await ApiService.getGruppe(gruppe.id);
      onRefresh(updated);
    } catch (e) {
      console.error(e);
    }
  };

  const sessions = useMemo(
    () => [...(gruppe.sessions || [])].sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime()),
    [gruppe.sessions]
  );

  const runningSession = sessions.find((s) => !s.ended_at && !(s.runden || []).some((r) => (r.punkte || []).some((p) => p.wert >= 100)));

  const handleCreateSpieler = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSpielerName.trim()) return;
    setIsCreatingSpieler(true);
    try {
      await ApiService.createSpieler(gruppe.id, newSpielerName.trim());
      setNewSpielerName('');
      await refresh();
      toast.success('Spieler hinzugefügt');
    } catch (e) {
      console.error(e);
      toast.error('Spieler konnte nicht hinzugefügt werden');
    } finally {
      setIsCreatingSpieler(false);
    }
  };

  const handleDeleteSpieler = async () => {
    if (!deleteSpieler) return;
    try {
      await ApiService.deleteSpieler(deleteSpieler.id);
      await refresh();
      toast.success('Spieler entfernt');
    } catch (e) {
      console.error(e);
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const handleDeleteSession = async () => {
    if (!deleteSession) return;
    try {
      await ApiService.deleteSession(deleteSession.id);
      await refresh();
      toast.success('Session gelöscht');
    } catch (e) {
      console.error(e);
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const formatDate = (s: string) =>
    new Date(s).toLocaleString('de-DE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

  return (
    <>
      <div className="large-title-row">
        <h1 className="large-title">{gruppe.name}</h1>
      </div>

      <div className="cta-card">
        <div className="cta-title">Bereit für die nächste Runde?</div>
        <div className="cta-sub">Starte ein neues Spiel oder schau dir die Bestenliste an.</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-glass" onClick={onNewSession}>
            <Icon name="play" size={18} />
            Neues Spiel
          </button>
          <button className="btn btn-glass" onClick={onShowLeaderboard}>
            <Icon name="trophy" size={18} />
            Leaderboard
          </button>
        </div>
      </div>

      <div className="bento-grid">
        <div className="bento glassy">
          <div className="bento-label">Spieler</div>
          <div className="bento-value">{gruppe.spieler?.length ?? 0}</div>
          <div className="bento-sub">im Team</div>
        </div>
        <div className="bento glassy">
          <div className="bento-label">Sessions</div>
          <div className="bento-value">{sessions.length}</div>
          <div className="bento-sub">{runningSession ? 'aktive Runde' : 'beendet'}</div>
        </div>
      </div>

      <SegmentedControl<Tab>
        value={tab}
        onChange={setTab}
        options={[
          { id: 'overview', label: 'Übersicht' },
          { id: 'sessions', label: 'Sessions' },
          { id: 'spieler', label: 'Spieler' },
        ]}
      />

      {tab === 'overview' && (
        <>
          {runningSession && (
            <>
              <div className="section-header">Aktive Session</div>
              <div className="ios-list">
                <div
                  className="ios-row interactive"
                  onClick={() => onSessionSelect(runningSession)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="row-icon success">
                    <Icon name="play" size={16} />
                  </div>
                  <div className="row-main">
                    <div className="row-title">Fortsetzen</div>
                    <div className="row-subtitle">
                      {formatDate(runningSession.started_at)} · {(runningSession.runden || []).length} Runden
                    </div>
                  </div>
                  <Icon name="chevron-right" size={16} className="chevron" />
                </div>
              </div>
            </>
          )}

          <div className="section-header">Schnellzugriff</div>
          <div className="ios-list">
            <div className="ios-row interactive" onClick={onNewSession} role="button" tabIndex={0}>
              <div className="row-icon"><Icon name="play" size={16} /></div>
              <div className="row-main"><div className="row-title">Neues Spiel starten</div></div>
              <Icon name="chevron-right" size={16} className="chevron" />
            </div>
            <div className="ios-row interactive" onClick={onShowLeaderboard} role="button" tabIndex={0}>
              <div className="row-icon warning"><Icon name="trophy" size={16} /></div>
              <div className="row-main"><div className="row-title">Leaderboard</div></div>
              <Icon name="chevron-right" size={16} className="chevron" />
            </div>
          </div>
        </>
      )}

      {tab === 'sessions' && (
        <>
          <div className="section-header">Alle Sessions</div>
          {sessions.length === 0 ? (
            <div className="hint-card">Noch keine Sessions – starte oben dein erstes Spiel.</div>
          ) : (
            <div className="ios-list">
              {sessions.map((s) => {
                const hatVerlierer = (s.runden || []).some((r) => (r.punkte || []).some((p) => p.wert >= 100));
                const istBeendet = !!s.ended_at || hatVerlierer;
                return (
                  <div key={s.id} className="ios-row">
                    <div className={`row-icon ${istBeendet ? 'danger' : 'success'}`}>
                      <Icon name={istBeendet ? 'check' : 'play'} size={16} />
                    </div>
                    <div className="row-main" style={{ cursor: 'pointer' }} onClick={() => onSessionSelect(s)}>
                      <div className="row-title">{formatDate(s.started_at)}</div>
                      <div className="row-subtitle">
                        {(s.runden || []).length} Runden · {istBeendet ? 'Beendet' : 'Läuft'}
                      </div>
                    </div>
                    <div className="row-aside">
                      <button className="btn btn-sm btn-tinted" onClick={() => onSessionSelect(s)}>
                        {istBeendet ? 'Ansehen' : 'Fortsetzen'}
                      </button>
                      <button
                        className="btn btn-sm btn-destructive-tinted btn-icon"
                        onClick={() => setDeleteSessionState(s)}
                        aria-label="Session löschen"
                      >
                        <Icon name="trash" size={18} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {tab === 'spieler' && (
        <>
          <form onSubmit={handleCreateSpieler} className="lg" style={{ padding: 12, marginBottom: 12 }}>
            <div className="input-row">
              <input
                className="input"
                type="text"
                value={newSpielerName}
                onChange={(e) => setNewSpielerName(e.target.value)}
                placeholder="Spielername…"
                disabled={isCreatingSpieler}
              />
              <button
                className="btn btn-primary btn-sm"
                type="submit"
                disabled={isCreatingSpieler || !newSpielerName.trim()}
                aria-label="Spieler hinzufügen"
              >
                <Icon name="plus" size={18} />
                <span className="hide-sm">Hinzufügen</span>
              </button>
            </div>
          </form>

          {(gruppe.spieler?.length ?? 0) === 0 ? (
            <div className="hint-card">Füge oben deine Spieler hinzu.</div>
          ) : (
            <>
              <div className="section-header">Spieler</div>
              <div className="ios-list">
                {gruppe.spieler.map((s) => (
                  <div key={s.id} className="ios-row">
                    <div className="row-icon purple">
                      <Icon name="person" size={16} />
                    </div>
                    <div className="row-main">
                      <div className="row-title">{s.name}</div>
                    </div>
                    <button
                      className="row-destructive"
                      onClick={() => setDeleteSpieler({ id: s.id, name: s.name })}
                    >
                      Löschen
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      <ActionSheet
        open={!!deleteSpieler}
        onClose={() => setDeleteSpieler(null)}
        title="Spieler entfernen?"
        message={deleteSpieler ? `„${deleteSpieler.name}“ wird aus der Gruppe entfernt.` : undefined}
        actions={[
          { label: 'Entfernen', style: 'destructive', onClick: handleDeleteSpieler },
          { label: 'Abbrechen', style: 'cancel' },
        ]}
      />

      <ActionSheet
        open={!!deleteSession}
        onClose={() => setDeleteSessionState(null)}
        title="Session löschen?"
        message="Alle Runden und Punkte dieser Session gehen verloren."
        actions={[
          { label: 'Löschen', style: 'destructive', onClick: handleDeleteSession },
          { label: 'Abbrechen', style: 'cancel' },
        ]}
      />
    </>
  );
};

export default GruppeDetails;
